/**
 * Hook for fetching and managing calendar events
 */

import { useMemo } from 'react'
import { trpc } from '@/trpc/client'
import {
	formatDateForApi,
	parseDateFromApi,
	parseTimeToDate,
} from '@/lib/calendar-utils'
import type {
	AppointmentType,
	CalendarEvent,
	ServerAppointmentLight,
} from '@/types/calendar'

/**
 * Transform lightweight server appointment to calendar event
 */
function transformToCalendarEvent(apt: ServerAppointmentLight): CalendarEvent {
	const date = parseDateFromApi(apt.date)
	const start = parseTimeToDate(date, apt.startTime)
	const end = parseTimeToDate(date, apt.endTime)

	return {
		id: apt.id,
		title: apt.title || 'Bez názvu',
		start,
		end,
		resource: apt,
	}
}

interface UseCalendarEventsOptions {
	type: AppointmentType
	from: Date
	to: Date
	enabled?: boolean
}

/**
 * Fetch calendar events for a date range
 */
export function useCalendarEvents({
	type,
	from,
	to,
	enabled = true,
}: UseCalendarEventsOptions) {
	const query = trpc.calendar.appointment.list.useQuery(
		{
			from: formatDateForApi(from),
			to: formatDateForApi(to),
			type,
		},
		{
			enabled,
			staleTime: 30000, // 30 seconds
			refetchOnWindowFocus: true,
		},
	)

	const events = useMemo(() => {
		if (!query.data) return []
		return query.data.map(transformToCalendarEvent)
	}, [query.data])

	return {
		events,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
	}
}

/**
 * Fetch repeating events
 */
export function useRepeatingEvents(type: AppointmentType) {
	const query = trpc.calendar.appointment.listRepeating.useQuery(undefined, {
		staleTime: 60000, // 1 minute
	})

	const events = useMemo(() => {
		if (!query.data) return []
		return query.data
			.filter((apt) => apt.type === type)
			.map(transformToCalendarEvent)
	}, [query.data, type])

	return {
		events,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
	}
}

/**
 * Search events by title with infinite scroll pagination
 */
export function useEventSearch(query: string, type?: AppointmentType) {
	const infiniteQuery = trpc.calendar.search.byTitle.useInfiniteQuery(
		{
			query,
			type,
			limit: 20,
		},
		{
			enabled: query.length >= 2,
			staleTime: 10000, // 10 seconds
			getNextPageParam: (lastPage) => lastPage.nextCursor,
		},
	)

	const events = useMemo(() => {
		if (!infiniteQuery.data) return []
		return infiniteQuery.data.pages
			.flatMap((page) => page.items)
			.map(transformToCalendarEvent)
	}, [infiniteQuery.data])

	return {
		events,
		isLoading: infiniteQuery.isLoading,
		isError: infiniteQuery.isError,
		hasNextPage: infiniteQuery.hasNextPage,
		isFetchingNextPage: infiniteQuery.isFetchingNextPage,
		fetchNextPage: infiniteQuery.fetchNextPage,
	}
}

/**
 * Fetch single appointment details (full data with patient details)
 */
export function useAppointmentDetail(appointmentId: string | null) {
	const query = trpc.calendar.appointment.getById.useQuery(
		{ id: appointmentId! },
		{
			enabled: !!appointmentId,
			staleTime: 10000, // 10 seconds
		},
	)

	return {
		appointment: query.data ?? null,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
	}
}

/**
 * Get appointments by patient
 */
export function usePatientAppointments(
	patientId: string,
	options?: { type?: AppointmentType; futureOnly?: boolean },
) {
	const query = trpc.calendar.appointment.byPatient.useQuery(
		{
			patientId,
			type: options?.type,
			futureOnly: options?.futureOnly ?? false,
		},
		{
			enabled: !!patientId,
			staleTime: 30000,
		},
	)

	const events = useMemo(() => {
		if (!query.data) return []
		return query.data.map(transformToCalendarEvent)
	}, [query.data])

	return {
		events,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
	}
}

/**
 * Day stats type
 */
export interface DayStats {
	patientCount: number
	ppCount: number
}

/**
 * Fetch day statistics (patient count, PP count) for a date range
 */
export function useDayStats({
	type,
	dates,
	enabled = true,
}: {
	type: AppointmentType
	dates: Date[]
	enabled?: boolean
}) {
	const query = trpc.calendar.appointment.dayStats.useQuery(
		{
			dates,
			type,
		},
		{
			enabled: enabled && dates.length > 0,
			staleTime: 30000, // 30 seconds
			refetchOnWindowFocus: true,
		},
	)

	return {
		stats: query.data ?? ({} as Record<string, DayStats>),
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
	}
}
